import { captureRemixErrorBoundaryError } from '@sentry/remix';
import type React from 'react';
import {
  Link,
  isRouteErrorResponse,
  useParams,
  useRouteError,
  type ErrorResponse,
} from 'react-router';

import { getErrorData } from '~/utils/misc.ts';

import { Button } from './ui/button';

type StatusHandler = (info: {
  error: ErrorResponse;
  params: Record<string, string | undefined>;
}) => React.ReactNode | null;

interface Props {
  defaultStatusHandler?: StatusHandler;
  statusHandlers?: Record<number, StatusHandler>;
}

// function UnexpectedErrorMessage({ error }: { error: unknown }) {
//   return <p>{getErrorMessage(error)}</p>;
// }

export function RouteErrorMessage({
  error,
  children,
}: {
  error: ErrorResponse;
  params: Record<string, string | undefined>;
  children?: React.ReactNode;
}) {
  return (
    <>
      <h1 className="text-8xl text-slate-600">{error.status}</h1>
      <p className="text-gray-600">
        <strong>{error.statusText.length ? error.statusText : 'Error'}:</strong>{' '}
        {getErrorData(error)}
      </p>
      {children ? <div>{children}</div> : null}
    </>
  );
}

export function UserFriendlyErrorMessage({
  error,
}: {
  error: unknown;
  params: Record<string, string | undefined>;
}) {
  let message = 'Something went wrong.';
  if (isRouteErrorResponse(error)) {
    switch (error.status) {
      case 401:
        message = 'You must be signed in to view this page.';
        break;
      case 403:
        message = "You don't have permission to view this page.";
        break;
      case 404:
        message = "We can't seem to find the page you're looking for.";
        break;
      default:
        break;
    }
  }

  return (
    <div className="flex flex-col items-center justify-center gap-6">
      <h1 className="font-mono">Whoops!</h1>
      <p className="text-body1">{message}</p>
      <div>
        <Button asChild>
          <Link to="/">Go To Homepage</Link>
        </Button>
      </div>
    </div>
  );
}

export function GeneralErrorBoundary({ defaultStatusHandler, statusHandlers }: Props) {
  const error = useRouteError();
  captureRemixErrorBoundaryError(error);
  const params = useParams();

  if (typeof document !== 'undefined') {
    console.error(error);
  }

  const statusHandler =
    isRouteErrorResponse(error) && (statusHandlers?.[error.status] ?? defaultStatusHandler);

  return (
    <div className="relative size-full overflow-hidden before:absolute before:left-0 before:top-0 before:h-full before:w-full before:bg-[url(/img/empty-room.jpg)] before:bg-cover before:bg-[center] before:bg-no-repeat before:brightness-[1] before:content-['']">
      <div className="container relative z-10 mx-auto flex h-full flex-col items-center justify-center gap-4 p-20">
        {statusHandler ? (
          statusHandler({ error, params })
        ) : (
          <UserFriendlyErrorMessage error={error} params={params} />
        )}
      </div>
    </div>
  );
}
